/**
 * ApiKeysPage — self-service API key management for the logged-in user.
 *
 * Lists active and revoked keys from GET /api/keys, lets the user issue
 * a new community-tier key (POST /api/keys), and revoke any existing
 * key (POST /api/keys/:id/revoke).
 *
 * The plaintext secret is returned exactly once from the issue endpoint
 * and is never recoverable after the user dismisses the issue modal.
 * The modal copy is explicit about that — copy-to-clipboard is the only
 * action the user can sensibly take.
 *
 * Styling mirrors the other /admin/* pages (Tailwind cosmos palette).
 * Route: /admin/api-keys (wired in App.tsx).
 */

import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { apiPath } from '../api.js';

type ApiKeyListItem = {
  key_id: string;
  key_prefix: string;
  tier: 'community' | 'commercial' | 'licensee';
  scopes: readonly string[];
  rate_limit_rpm: number;
  status: 'active' | 'revoked';
  created_at: number;
  last_used_at: number | null;
  revoked_at: number | null;
  notes: string | null;
};

type IssuedKey = {
  key_id: string;
  secret: string;
  prefix: string;
  tier: 'community';
  scopes: readonly string[];
  rate_limit_rpm: number;
  status: 'active';
  created_at: number;
  notes: string | null;
};

function formatTime(seconds: number | null): string {
  if (seconds === null) return '—';
  return new Date(seconds * 1000).toISOString().replace('T', ' ').replace('.000Z', 'Z');
}

export function ApiKeysPage(): ReactElement {
  const [keys, setKeys] = useState<readonly ApiKeyListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newNotes, setNewNotes] = useState('');
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(apiPath('/api/keys'), { credentials: 'include' });
      if (!r.ok) {
        setError(`Failed to load keys: HTTP ${r.status}`);
        return;
      }
      const body = (await r.json()) as { ok: boolean; data?: { keys: ApiKeyListItem[] } };
      if (!body.ok || !body.data) {
        setError('Failed to load keys');
        return;
      }
      setKeys(body.data.keys);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const issue = useCallback(async () => {
    setError(null);
    setCreating(true);
    try {
      const r = await fetch(apiPath('/api/keys'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ notes: newNotes.trim() || undefined }),
      });
      const body = (await r.json()) as { ok: boolean; data?: IssuedKey; error?: string };
      if (!r.ok || !body.ok || !body.data) {
        setError(body.error ?? `HTTP ${r.status}`);
        return;
      }
      setIssued(body.data);
      setNewNotes('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }, [newNotes, refresh]);

  const revoke = useCallback(
    async (keyId: string) => {
      setError(null);
      setRevoking(keyId);
      try {
        const r = await fetch(apiPath(`/api/keys/${keyId}/revoke`), {
          method: 'POST',
          credentials: 'include',
        });
        if (!r.ok) {
          setError(`Revoke failed: HTTP ${r.status}`);
          return;
        }
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setRevoking(null);
      }
    },
    [refresh],
  );

  return (
    <div className="mx-auto max-w-4xl p-6 text-cosmos-100">
      <header className="mb-4">
        <h1 className="font-bold text-2xl text-amber-200">API Keys</h1>
        <p className="mt-2 text-cosmos-300 text-sm">
          Issue and manage API keys for the <code className="text-amber-200">/api/v1/*</code>{' '}
          community surface. Keys carry the same identity as your account; revoke any key you no
          longer recognize.
        </p>
      </header>

      {error ? (
        <div className="mb-4 rounded border border-red-700 bg-red-950/40 p-3 text-red-200 text-sm">
          {error}
        </div>
      ) : null}

      <section className="mb-6 rounded border border-cosmos-700 bg-cosmos-900 p-4">
        <h2 className="mb-2 font-semibold text-amber-200">Issue a new key</h2>
        <p className="mb-3 text-cosmos-300 text-sm">
          Community tier · 60 requests/minute · read access to cards, rules, public decks.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            placeholder="Notes (optional, e.g. 'deck-builder tool')"
            maxLength={280}
            className="flex-1 rounded border border-cosmos-700 bg-cosmos-950 px-3 py-1.5 text-sm focus:border-amber-500 focus:outline-none"
            disabled={creating}
          />
          <button
            type="button"
            onClick={() => void issue()}
            disabled={creating}
            className="rounded bg-amber-600 px-4 py-1.5 font-medium text-cosmos-950 text-sm hover:bg-amber-500 disabled:opacity-50"
          >
            {creating ? 'Issuing…' : 'Issue key'}
          </button>
        </div>
      </section>

      <section>
        <h2 className="mb-2 font-semibold text-amber-200">Your keys</h2>
        {keys === null ? (
          <p className="text-cosmos-400 text-sm">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="text-cosmos-400 text-sm">No keys yet. Issue one above.</p>
        ) : (
          <ul className="space-y-2">
            {keys.map((k) => (
              <li
                key={k.key_id}
                className={`rounded border p-3 ${
                  k.status === 'active'
                    ? 'border-cosmos-700 bg-cosmos-900'
                    : 'border-cosmos-800 bg-cosmos-950 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-amber-200">{k.key_prefix}…</code>
                      <span
                        className={`rounded px-2 py-0.5 text-xs ${
                          k.status === 'active'
                            ? 'bg-emerald-700 text-emerald-100'
                            : 'bg-cosmos-700 text-cosmos-300'
                        }`}
                      >
                        {k.status}
                      </span>
                      <span className="rounded bg-cosmos-800 px-2 py-0.5 text-cosmos-300 text-xs">
                        {k.tier}
                      </span>
                      <span className="text-cosmos-400 text-xs">{k.rate_limit_rpm} rpm</span>
                    </div>
                    <div className="mt-1 text-cosmos-400 text-xs">
                      Created {formatTime(k.created_at)} · Last used {formatTime(k.last_used_at)}
                      {k.revoked_at !== null ? ` · Revoked ${formatTime(k.revoked_at)}` : ''}
                    </div>
                    {k.notes ? <div className="mt-1 text-cosmos-300 text-sm">{k.notes}</div> : null}
                    <div className="mt-1 text-cosmos-400 text-xs">
                      Scopes: {k.scopes.join(', ')}
                    </div>
                  </div>
                  {k.status === 'active' ? (
                    <button
                      type="button"
                      onClick={() => void revoke(k.key_id)}
                      disabled={revoking === k.key_id}
                      className="rounded border border-red-700 px-3 py-1 text-red-300 text-xs hover:bg-red-950/40 disabled:opacity-50"
                    >
                      {revoking === k.key_id ? 'Revoking…' : 'Revoke'}
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {issued ? <IssuedKeyModal issued={issued} onClose={() => setIssued(null)} /> : null}
    </div>
  );
}

function IssuedKeyModal({
  issued,
  onClose,
}: {
  issued: IssuedKey;
  onClose: () => void;
}): ReactElement {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be unavailable in some contexts — user can manually copy
    }
  }, [issued.secret]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded border border-amber-600 bg-cosmos-900 p-6 text-cosmos-100">
        <h2 className="mb-2 font-bold text-amber-200 text-xl">Your new API key</h2>
        <p className="mb-4 text-cosmos-300 text-sm">
          Copy this secret now.{' '}
          <strong className="text-amber-200">It will not be shown again.</strong> We store only a
          hash; if you lose the key, you'll need to issue a new one.
        </p>
        <div className="mb-4 break-all rounded border border-cosmos-700 bg-cosmos-950 p-3 font-mono text-amber-200 text-sm">
          {issued.secret}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void copy()}
            className="rounded bg-amber-600 px-4 py-1.5 font-medium text-cosmos-950 text-sm hover:bg-amber-500"
          >
            {copied ? 'Copied!' : 'Copy to clipboard'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-cosmos-700 px-4 py-1.5 text-cosmos-200 text-sm hover:bg-cosmos-800"
          >
            I've saved it
          </button>
        </div>
      </div>
    </div>
  );
}
